/** Radix Dialog üzerine kurulu form diyaloğu ve yıkıcı işlem onayı. */

import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { M } from "../dil";
import { Button } from "./primitives";

const overlayClass = "fixed inset-0 z-40 bg-[var(--pf-overlay)]";
// Yükseklik `.pf-pencere`de (görünen alana göre); form düğmeleri `.pf-pencere-alt`
// ile altta sabit (telefon, 04.11.2026).
const contentClass =
  "pf-pencere fixed left-1/2 top-1/2 z-50 w-[min(46rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 " +
  "overflow-auto rounded-pf border border-line bg-surface p-4 shadow-[var(--pf-shadow-2)]";

export interface ModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}

export function Modal({ open, onOpenChange, title, description, children }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlayClass} />
        <Dialog.Content className={contentClass}>
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="text-[15px] font-semibold text-ink">{title}</Dialog.Title>
            {/* Her pencerenin bir çıkışı: düğmeler görünmese bile (telefon, 04.11.2026). */}
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label={M.ortak.kapat}
                className="-m-1 shrink-0 rounded-pf p-1 text-muted hover:bg-surface-2 hover:text-ink"
              >
                <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </Dialog.Close>
          </div>
          {description === undefined ? (
            <Dialog.Description className="sr-only">{title}</Dialog.Description>
          ) : (
            <Dialog.Description className="mt-1 text-[12px] text-muted">{description}</Dialog.Description>
          )}
          <div className="mt-3">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ConfirmProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly body: ReactNode;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly pending?: boolean;
  /** Onay düğmesi kapalı (ör. seçim eksik). */
  readonly disabled?: boolean;
}

export function Confirm({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  onConfirm,
  pending = false,
  disabled = false,
}: ConfirmProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={overlayClass} />
        <AlertDialog.Content className={contentClass + " w-[min(28rem,calc(100vw-2rem))]"}>
          <AlertDialog.Title className="text-[15px] font-semibold text-ink">{title}</AlertDialog.Title>
          <AlertDialog.Description asChild>
            <div className="mt-2 text-[13px] text-muted">{body}</div>
          </AlertDialog.Description>
          <div className="pf-pencere-alt mt-4 flex justify-end gap-2">
            <AlertDialog.Cancel asChild>
              <Button>{M.ortak.vazgec}</Button>
            </AlertDialog.Cancel>
            <Button variant="danger" onClick={onConfirm} disabled={pending || disabled}>
              {pending ? M.ortak.siliniyor : confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
